/**
 * Кольцевая диаграмма долей.
 *
 * Сегменты рисуются штрихом по одной окружности через stroke-dasharray:
 * это одна фигура вместо дуг с ручной тригонометрией, и попадание
 * курсора совпадает с видимой толщиной кольца.
 *
 * Сегмент кликабелен, когда передан onSelect — тогда это не картинка,
 * а вход в отфильтрованный список.
 */

import { Vars } from '../Vars'

const TONE_COLORS = {
  accent: 'var(--accent)',
  success: 'var(--success)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  info: 'var(--info)',
  neutral: 'var(--neutral)',
}

const R = 60
const CIRC = 2 * Math.PI * R

export function DonutChart({
  segments = [],
  total,
  centerLabel = 'всего',
  onSelect,
  size = 160,
}) {
  const sum = total ?? segments.reduce((acc, s) => acc + (s.value || 0), 0)

  if (!sum) {
    return (
      <div className="donut donut--empty">
        <p className="text-muted">Нет данных</p>
      </div>
    )
  }

  let offset = 0
  const arcs = segments
    .filter((s) => s.value > 0)
    .map((s) => {
      const share = s.value / sum
      const arc = {
        ...s,
        share,
        dash: share * CIRC,
        offset,
        color: TONE_COLORS[s.tone] || TONE_COLORS.neutral,
      }
      offset += arc.dash
      return arc
    })

  return (
    <div className="donut">
      <svg
        className="donut__svg"
        width={size}
        height={size}
        viewBox="0 0 160 160"
        role="img"
        aria-label={`Всего ${sum}. ${arcs
          .map((a) => `${a.label}: ${a.value}`)
          .join(', ')}`}
      >
        <circle cx="80" cy="80" r={R} fill="none" stroke="var(--bg-sunken)" strokeWidth="18" />
        {arcs.map((a) => (
          <circle
            key={a.key}
            cx="80"
            cy="80"
            r={R}
            fill="none"
            stroke={a.color}
            strokeWidth="18"
            strokeDasharray={`${a.dash} ${CIRC - a.dash}`}
            strokeDashoffset={-a.offset}
            transform="rotate(-90 80 80)"
          >
            <title>
              {a.label}: {a.value} ({Math.round(a.share * 100)}%)
            </title>
          </circle>
        ))}
        <text x="80" y="74" className="donut__total" textAnchor="middle">
          {sum}
        </text>
        <text x="80" y="94" className="donut__caption" textAnchor="middle">
          {centerLabel}
        </text>
      </svg>

      <ul className="donut__legend">
        {segments.map((s) => {
          const pct = sum ? Math.round((s.value / sum) * 100) : 0
          const content = (
            <>
              <Vars
                as="span"
                className="donut__dot"
                vars={{ '--dot-color': TONE_COLORS[s.tone] || TONE_COLORS.neutral }}
              />
              <span className="donut__legend-label">{s.label}</span>
              <span className="donut__legend-value">
                {s.value}
                <span className="donut__legend-pct">{pct}%</span>
              </span>
            </>
          )

          return (
            <li key={s.key}>
              {onSelect ? (
                <button
                  type="button"
                  className="donut__legend-row donut__legend-row--action"
                  onClick={() => onSelect(s)}
                >
                  {content}
                </button>
              ) : (
                <span className="donut__legend-row">{content}</span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
